import styled from "@emotion/styled"
import { DateTime } from "luxon"
import { useEffect, useState, useRef, useLayoutEffect } from "react"

import { bold, normal } from "css/text"
import { lastFullMoonBefore } from "./lunar"
import { useConsole } from "contexts/Console"
import { useMotionValue, useMotionValueEvent } from "framer-motion"
import { Icon } from "components/icon/Icon"
import getMediaQuery from "css/breakpoints"
import { observeResize } from "./context"

const Instructions = styled.div`
  margin-block-start: 20px;
  padding-inline: 20px;
  --mc-expand: 0;

  &.expand {
    cursor: pointer;
    pointer-events: auto;
    --mc-expand: 1;
    
    ${getMediaQuery("m")} {
      cursor: default;
      pointer-events: none;
      --mc-expand: 0;
    }
  }
`

const InstructionNote = styled.div`
  align-items: center;
  backdrop-filter: blur(5px);
  background: rgb(212 212 212 / 0.3);
  border-radius: 10px;
  display: flex;
  gap: 15px;
  padding: 10px;
  align-items: stretch;

  ${getMediaQuery("s")} {
    gap: 20px;
    padding: 20px;
  }

  html.prefers-contrast & {
    color: black !important;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 0px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 0px);
      z-index: -1;
    }
  }
`

const InstructionIcon = styled.span`
  align-items: center;
  aspect-ratio: 1;
  background: rgb(var(--pure-white));
  border-radius: 5px;
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  inline-size: 60px;
  justify-content: center;
  padding: 5px;
`

const InstructionCopy = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr max-content;
  gap: 5px;
//  min-inline-size: 0;

  & svg {
    width: 1.2rem;
    height: 1.2rem;
    margin-inline: 5px;
    grid-row: 1;
    grid-column: 2;
    align-self: center;
    transform: rotate(0deg);
    transition: transform .3s linear;
    display: none;
    justify-self: end;
  }

  ${getMediaQuery("m")} {
     gap: 10px;
  }

  .expand & {
    grid-template-rows: auto 0fr;
    transition: grid-template-rows .3s ease-out;
    svg {
      display: block;
    }

    ${getMediaQuery("m")} {
      grid-template-rows: max-content 1fr;
      transition: none;
      svg {
        display: none;
      }
    }
  }

  .expand[aria-expanded="true"] & {
      grid-template-rows: max-content 1fr;

      svg {
        transform: rotate(180deg);
      }
    }
  }
`
/*
const InstructionCopyOld = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-inline-size: 0;
`
*/
const InstructionTitle = styled.h2`
  ${bold}
  font-size: 16px;
  line-height: 22px;
  margin: 0;
  grid-row: 1;
  grid-column: 1;
  align-self: center;
`

const InstructionList = styled.ul`
  ${normal}
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  list-style-type: decimal;
  padding-inline-start: 20px;
  grid-row: 2;
  grid-column: 1 / 2;
  overflow: hidden;

  &:lang(ar),
  &:lang(fa) {
    list-style-type: arabic-indic; 
  }
`
const InstructionItem = styled.li``

function formatDaysSince({ group, isFullMoonToday, locale, value }) {
  const labels = group?.[0] ?? group
  const pluralForm = isFullMoonToday ? "zero" : new Intl.PluralRules(locale).select(value)
  const template = labels?.[pluralForm] ?? labels?.other

  return template?.replaceAll("{{press}}", value) ?? ""
}

function getDateTime(timestamp, timezone) {
  const date = DateTime.fromMillis(timestamp, { zone: timezone })
  return date.isValid ? date : DateTime.fromMillis(timestamp)
}

export function getMoonphaseInstructionState(timestamp, timezone = "local") {
  const currentDay = getDateTime(timestamp, timezone).startOf("day")
  const fullMoonDay = DateTime.fromJSDate(lastFullMoonBefore(currentDay.endOf("day").toJSDate()), { zone: currentDay.zoneName }).startOf("day")

  return {
    daysSinceFullMoon: Math.max(0, Math.round(currentDay.diff(fullMoonDay, "days").days)),
    isFullMoonToday: currentDay.hasSame(fullMoonDay, "day"),
  }
}

function useReferenceTimestamp(timestamp, timezone) {
  const [currentTimestamp, setCurrentTimestamp] = useState(() => timestamp ?? Date.now())

  useEffect(() => {
    if (timestamp != null) return

    let timeout

    const scheduleNextDay = () => {
      const now = getDateTime(Date.now(), timezone)
      const delay = Math.max(1000, now.plus({ days: 1 }).startOf("day").diff(now).as("milliseconds"))

      timeout = setTimeout(() => {
        setCurrentTimestamp(Date.now())
        scheduleNextDay()
      }, delay)
    }

    setCurrentTimestamp(Date.now())
    scheduleNextDay()

    return () => clearTimeout(timeout)
  }, [timestamp, timezone])

  return timestamp ?? currentTimestamp
}

export default function MoonphaseInstructions({ className, labels, locale = "en", timestamp, timezone = "local" }) {
  const console = useConsole()
  const referenceTimestamp = useReferenceTimestamp(timestamp, timezone)
  const { daysSinceFullMoon, isFullMoonToday } = getMoonphaseInstructionState(referenceTimestamp, timezone)
  const instructions = formatDaysSince({
    group: labels?.days_since,
    isFullMoonToday,
    locale,
    value: daysSinceFullMoon,
  })

  const rfmain = useRef(null)
  const expanded = useMotionValue(0)

  function onChange(v) {
    rfmain.current.setAttribute("aria-expanded", !!v)
  }
  useMotionValueEvent(expanded, "change", onChange)

  const is_button = useMotionValue(-1)
  function onIsBtn(v) {
    if (!v) {
      rfmain.current.removeAttribute("role")
      rfmain.current.removeAttribute("aria-expanded")
      return
    }
    rfmain.current.setAttribute("role", "button")
    rfmain.current.setAttribute("aria-expanded", "false")
  }
  useMotionValueEvent(is_button, "change", onIsBtn)
  function onResize({ target }) {
    is_button.set(parseInt(getComputedStyle(target).getPropertyValue("--mc-expand")))
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize))

  function onClick(e) {
    if (is_button.get() <= 0) return
    expanded.set(1 - expanded.get())
  }

  return (
    <Instructions
      className={className}
      onClick={onClick}
      aria-expanded="true"
      ref={rfmain}
    >
      <InstructionNote>
        <InstructionIcon>
          <svg aria-hidden='true' fill='none' height='30' viewBox='0 0 24 30' width='24'>
            <ellipse cx='10.668' cy='15.0058' fill='#233752' rx='10.668' ry='10.668' />
            <circle cx='10.6708' cy='15.0077' fill='#233752' r='8.27153' stroke='#fff' strokeWidth='.77304' />
            <g fill='#fff'>
              <path d='m10.2849 7.89569c0 .21347.173.38652.3865.38652s.3865-.17305.3865-.38652h-.3865zm.3865-1.54608h-.3865v1.54608h.3865.3865v-1.54608z' />
              <path d='m17.784 14.6203c-.2135 0-.3865.1731-.3865.3865 0 .2135.173.3866.3865.3866v-.3866zm1.5461.3865v-.3865h-1.5461v.3865.3866h1.5461z' />
              <path d='m16.5274 10.9529c-.1819.1118-.2387.3499-.1269.5317.1118.1819.3498.2387.5317.1269l-.2024-.3293zm1.5196-.4802-.2024-.3293-1.3172.8095.2024.3293.2024.3293 1.3172-.8096z' />
              <path d='m16.8916 18.4819c-.1804-.1141-.4191-.0603-.5332.1202-.1141.1804-.0603.4191.1202.5332l.2065-.3267zm1.1003 1.1529.2066-.3267-1.3069-.8262-.2065.3267-.2065.3267 1.3068.8262z' />
              <path d='m7.44982 21.3602c.10673-.1849.04339-.4213-.14148-.528-.18487-.1068-.42126-.0434-.528.1415l.33474.1932zm-1.10778 1.1457.33474.1932.77304-1.3389-.33474-.1933-.33474-.1932-.77304 1.3389z' />
              <path d='m6.78034 9.04392c.10674.18487.34313.24821.528.14148.18487-.10674.24821-.34313.14148-.528l-.33474.19326zm-.4383-1.5322-.33474.19326.77304 1.33894.33474-.19326.33474-.19326-.77304-1.33894z' />
              <path d='m10.2849 22.118c0-.2135.173-.3865.3865-.3865s.3865.173.3865.3865h-.3865zm.3865 1.5461h-.3865v-1.5461h.3865.3865v1.5461z' />
              <path d='m3.55878 14.6203c.21346 0 .38652.1731.38652.3865 0 .2135-.17306.3866-.38652.3866v-.3866zm-1.54608.3865v-.3865h1.54608v.3865.3866h-1.54608z' />
              <path d='m4.41067 18.4021c.18187-.1117.41991-.0549.53169.1269.11178.1819.05497.4199-.1269.5317l-.20239-.3293zm-1.11477 1.1389-.2024-.3293 1.31717-.8096.2024.3293.20239.3293-1.31717.8096z' />
              <path d='m4.86494 10.8803c.18043.1141.23424.3528.12017.5333-.11407.1804-.35282.2342-.53325.1201l.20654-.3267zm-1.51338-.4994.20654-.3267 1.30684.8261-.20654.3267-.20654.3267-1.30684-.8261z' />
              <path d='m14.5632 9.04197c-.1068.18487-.3432.24821-.528.14148-.1849-.10674-.2482-.34313-.1415-.528l.3347.19326zm.4383-1.5322.3347.19326-.773 1.33894-.3348-.19326-.3347-.19326.773-1.33894z' />
              <path d='m13.8937 21.3612c-.1067-.1849-.0434-.4213.1415-.528.1848-.1068.4212-.0434.528.1414l-.3348.1933zm1.1078 1.1456-.3348.1933-.773-1.3389.3347-.1933.3348-.1933.773 1.339z' />
            </g>
            <circle cx='10.6732' cy='15.0067' r='.927648' stroke='#fff' strokeWidth='.77304' />
            <path
              d='m7.17348 12.1318c-.1791-.1162-.41845-.0652-.53462.1139s-.06516.4185.11393.5346l.21034-.3242zm2.6499 2.1796.21032-.3243-2.86022-1.8553-.21035.3243-.21034.3242 2.86025 1.8553z'
              fill='#fff'
            />
            <path
              d='m10.948 6.79297c0-.21347-.173-.38652-.3865-.38652-.2134 0-.3865.17305-.3865.38652h.3865zm-.3865 7.33423h.3865v-7.33423h-.3865-.3865v7.33423z'
              fill='#fff'
            />
            <path d='m22.6377 14.3135.0107 1.8784' stroke='#233752' strokeLinecap='round' strokeWidth='1.76021' />
            <path d='m6.25089 0h8.55341l2.407 4.96559-1.3712-.69585c-3.3509-1.70048-7.3003-1.75378-10.69589-.14434l-1.23225.58406z' fill='#233752' />
            <path d='m6.25089 30h8.55341l2.407-4.9656-1.3712.6959c-3.3509 1.7004-7.3003 1.7537-10.69589.1443l-1.23225-.5841z' fill='#233752' />
          </svg>
        </InstructionIcon>
        <InstructionCopy>
          <InstructionTitle>{labels?.how_to}</InstructionTitle>
          <Icon type='arrowUp' size={14} />
          <InstructionList>
            <InstructionItem>{labels?.position}</InstructionItem>
            {!isFullMoonToday ? <InstructionItem>{instructions}</InstructionItem> : null}
          </InstructionList>
        </InstructionCopy>
      </InstructionNote>
    </Instructions>
  )
}
